import { useAuth } from '@/contexts/AuthContext';
import { getPermissions, getScreens, getActions } from '@/lib/permissionsStore';

export interface PermissionsResult {
  canAccessScreen: (screenKey: string) => boolean;
  canPerformAction: (actionKey: string) => boolean;
  role: string | null;
}

const SCREEN_ROUTE_MAP: Record<string, string> = {
  '/': 'dashboard',
  '/entry': 'entry',
  '/shipments': 'shipments',
  '/follow-up': 'followUp',
  '/accounting': 'accounting',
  '/driver-app': 'driverApp',
  '/customer-app': 'customerApp',
  '/customers': 'customers',
  '/drivers': 'drivers',
  '/inventory': 'inventory',
  '/settings': 'settings',
};

export const getScreenKeyFromPath = (path: string): string | null => {
  // Handle /shipments/:id
  if (path.startsWith('/shipments/')) return 'shipments';
  return SCREEN_ROUTE_MAP[path] || null;
};

export const usePermissions = (): PermissionsResult => {
  const { role } = useAuth();

  const canAccessScreen = (screenKey: string): boolean => {
    if (!role) return true;
    if (role === 'admin') return true;
    if (role === 'driver' && screenKey === 'driverApp') return true;
    if (role === 'customer' && screenKey === 'customerApp') return true;
    const perms = getPermissions();
    return perms[role]?.screens?.[screenKey] !== false;
  };

  const canPerformAction = (actionKey: string): boolean => {
    if (!role) return true; // open access when no role
    if (role === 'admin') return true;
    const perms = getPermissions();
    return perms[role]?.actions?.[actionKey] !== false;
  };

  return { canAccessScreen, canPerformAction, role };
};
